import type {
  GetSoftTagResponse,
  Tags,
} from '@/features/tags/types/tags.types';
import type {
  CreateSoftTagDTO,
  CreateSoftTagParams,
  CreateSoftTagResponse,
  SoftTagState,
} from './types';
import type { Site } from '@/features/assetHierarchy/utils/assetHierarchy.types';
import { Colors } from '../../../../public/GlobalTokens';

export const mapCreateSoftTagDTOToResponse = (dto: CreateSoftTagDTO): any => {
  return { ...dto };
};

export const setSchedule = (
  schedule: string,
  schedulingInterval: string
): string => {
  if (schedule && schedulingInterval) {
    return `@every ${schedulingInterval}${schedule}`;
  }
  return '@never';
};

export const mapCreateSoftTagStateToParams = (
  state: SoftTagState
): CreateSoftTagParams => {
  return {
    dataSource: 'DPP',
    scope: 'global',
    reference: ['creation_test'],
    tag_name: state.softTagName,
    description: state.description,
    timeseries_type: state.signalName,
    data_type: state.dataType,
    unit: state.unit,
    signal_type: state.signalType,
    interval: setSchedule(state.schedule, state.schedulingInterval),
    production_unit: state.productionUnit,
    site: state.productionSite,
    area: state.area,
    created_by: state.owner,
    execution_code: state.executionCode,
  };
};

export const mapSoftTagsResponseToState = (
  response: CreateSoftTagResponse
): Tags => {
  const data = response.metadata.softTagPayload;
  return {
    id: Math.random(),
    timeseriesId: Math.random().toString(36).substring(2, 15),
    name: data.tag_name,
    description: data.description,
    status: 'pending',
    unit: data.timeseries.unit,
    productionUnit: data.production_unit,
    site: data.site,
    area: data.area,
    timeseriesType: data.timeseries.type,
    region: null,
    country: null,
    createdBy: 'SoftTagManager',
    createdAt: new Date().toISOString(),
    executions: [],
  };
};

const parseSchedule = (
  interval: string
): { schedule: string; schedulingInterval: string } => {
  if (interval && interval.startsWith('@every')) {
    const match = interval.match(/@every (\d+)([smhd])/);
    if (match) {
      return { schedulingInterval: match[1], schedule: match[2] };
    }
  }
  return { schedulingInterval: '', schedule: '' };
};

export const mapTagToSoftTagState = (tag: GetSoftTagResponse): SoftTagState => {
  const { schedule, schedulingInterval } = parseSchedule(tag.interval);
  return {
    softTagName: tag.name,
    description: tag.description,
    signalName: tag.timeseries_type, // Not available in Tags
    timeseriesId: tag.timeseries_id,
    dataType: tag.data_type?.toUpperCase(), // Not available in Tags
    unit: tag.unit,
    signalType: tag.signal_type?.toUpperCase(), // Not available in Tags
    schedule: schedule, // Not available in Tags
    schedulingInterval: schedulingInterval, // Not available in Tags
    productionUnit: tag.production_unit,
    productionSite: tag.site,
    area: tag.area,
    owner: tag.created_by,
    executionCode: tag.code, // Not available in Tags
  };
};

export const filterSites = (
  assetHierarchy: Site[],
  site: string,
  area: string,
  productionUnit: string
) => {
  const filteredSites = assetHierarchy.map(s => ({
    text: s.name,
    value: s.name,
  }));
  const filteredAreas = Array.from(
    new Set(
      assetHierarchy
        .filter(s => (site ? s.name === site : true))
        .flatMap(s => s.areas)
        .map(a => a.name)
    )
  ).map(name => ({ text: name, value: name }));
  const filteredUnits = Array.from(
    new Set(
      assetHierarchy
        .filter(s => (site ? s.name === site : true))
        .flatMap(s => s.areas)
        .filter(a => (area ? a.name === area : true))
        .flatMap(a => a.production_units)
        .map(pu => pu.name)
    )
  ).map(name => ({ text: name, value: name }));
  return {
    filteredSites,
    filteredAreas,
    filteredUnits,
  };
};

// Stateless toast logic using direct DOM manipulation
export function showCopyToast(
  container: HTMLDivElement,
  message: string = 'Copied',
  isError: boolean = false
) {
  const toast = document.createElement('div');
  toast.textContent = message;
  Object.assign(toast.style, {
    position: 'absolute',
    top: '-75%',
    left: '50%',
    transform: 'translateX(-50%)',
    background: isError ? '#dc2626' : Colors.colorType.brand.contrast,
    color: '#fff',
    borderRadius: '4px',
    padding: '2px 10px',
    fontSize: '12px',
    marginTop: '4px',
    zIndex: 100,
    whiteSpace: 'nowrap',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    opacity: '0',
    transition: 'opacity 0.2s ease-in-out',
  });

  container.appendChild(toast);

  // Animate in
  setTimeout(() => {
    toast.style.opacity = '1';
  }, 10);

  // Animate out and remove
  setTimeout(
    () => {
      toast.style.opacity = '0';
      setTimeout(() => {
        if (container.contains(toast)) {
          container.removeChild(toast);
        }
      }, 200);
    },
    isError ? 2000 : 400
  );
}
